import { useEffect, useState } from 'react';
import apiClient from '@/lib/api.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  nombre_comercial: string;
  dominio_slug: string;
  estado: 'active' | 'trial' | 'suspended';
  created_at: string;
}

interface TenantsResponse {
  status: string;
  data: Tenant[];
  total: number;
}

// ─── EstadoBadge ──────────────────────────────────────────────────────────────

function EstadoBadge({ estado }: { estado: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
  active:    { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: 'Activo' },
  trial:     { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', label: 'Trial' },
  suspended: { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', label: 'Suspendido' },
  };
  const s = map[estado] ?? map.suspended;
  return (
    <span style={{
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: '0.75rem',
      fontWeight: 500,
      background: s.bg,
      color: s.color,
    }}>
      {s.label}
    </span>
  );
}

// ─── AdminDashboardPage ───────────────────────────────────────────────────────

export function AdminDashboardPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [total, setTotal]     = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    async function fetchTenants() {
      try {
        setIsLoading(true);
        const res = await apiClient.get<TenantsResponse>('/admin/tenants?limit=100');
        setTenants(res.data.data ?? []);
        setTotal(res.data.total ?? 0);
      } catch (err: unknown) {
        console.log('[v0] AdminDashboard error:', err);
        const msg = err instanceof Error ? err.message : 'Error desconocido';
        setError(`No se pudieron cargar los datos del panel. (${msg})`);
      } finally {
        setIsLoading(false);
      }
    }
    fetchTenants();
  }, []);

  const activos     = tenants.filter(t => t.estado === 'active').length;
  const trial       = tenants.filter(t => t.estado === 'trial').length;
  const suspendidos = tenants.filter(t => t.estado === 'suspended').length;

  const stats = [
    { label: 'Total Tenants', value: total,       color: 'var(--color-primary)' },
    { label: 'Activos',       value: activos,     color: '#16a34a' },
    { label: 'En Trial',      value: trial,        color: '#d97706' },
    { label: 'Suspendidos',   value: suspendidos, color: '#dc2626' },
  ];

  const recent = [...tenants]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

      {/* Header */}
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>
          Panel Maestro
        </h1>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-muted)', marginTop: 4 }}>
          Vision global de la plataforma ImpulsaERP
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
        {isLoading
          ? [1, 2, 3, 4].map(i => (
              <div key={i} style={{
                borderRadius: 12,
                height: 100,
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                opacity: 0.5,
              }} />
            ))
          : stats.map(stat => (
              <div key={stat.label} style={{
                borderRadius: 12,
                padding: '1.5rem',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-muted)' }}>
                  {stat.label}
                </span>
                <span style={{ fontSize: '2rem', fontWeight: 700, color: stat.color }}>
                  {stat.value}
                </span>
              </div>
            ))
        }
      </div>

      {/* Error */}
      {error && (
        <div style={{
          borderRadius: 8,
          padding: '0.75rem 1rem',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          fontSize: '0.875rem',
        }}>
          {error}
        </div>
      )}

      {/* Tabla tenants recientes */}
      {!isLoading && !error && (
        <div style={{
          borderRadius: 12,
          overflow: 'hidden',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
        }}>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)' }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--color-text)' }}>
              Ultimos tenants registrados
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['Empresa', 'Slug', 'Estado', 'Alta'].map(h => (
                  <th key={h} style={{
                    textAlign: 'left',
                    padding: '0.75rem 1.5rem',
                    fontWeight: 500,
                    color: 'var(--color-muted)',
                    borderBottom: '1px solid var(--color-border)',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-muted)' }}>
                    No hay tenants registrados todavia.
                  </td>
                </tr>
              ) : (
                recent.map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.75rem 1.5rem', fontWeight: 500, color: 'var(--color-text)' }}>
                      {t.nombre_comercial}
                    </td>
                    <td style={{ padding: '0.75rem 1.5rem', fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                      {t.dominio_slug}
                    </td>
                    <td style={{ padding: '0.75rem 1.5rem' }}>
                      <EstadoBadge estado={t.estado} />
                    </td>
                    <td style={{ padding: '0.75rem 1.5rem', color: 'var(--color-muted)' }}>
                      {new Date(t.created_at).toLocaleDateString('es-ES')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
